/**
 * Scopes for Admin → Email Templates (#978). The page loads the org's whole
 * library once (`GET /api/admin/email-templates/all`) and filters here, so the
 * scope counts and the list always agree.
 *
 *  - Admin templates: the org's official set (`is_admin_template`).
 *  - Shared: a member's template shared with specific people.
 *  - Group: a member's template shared with at least one group.
 *  - Personal: a member's template nobody else can see.
 *
 * A template shared with people AND a group is in both Shared and Group.
 */
export const TEMPLATE_SCOPES = [
    { value: 'all', label: 'All', hint: 'Every template in your organisation, whoever owns it.' },
    { value: 'admin', label: 'Admin templates', hint: 'The official set. Every coach sees these under Organisation and in every composer, and can make a copy to edit.' },
    { value: 'shared', label: 'Shared', hint: 'Coach templates shared with specific people.' },
    { value: 'personal', label: 'Personal', hint: 'Coach templates nobody else can see.' },
    { value: 'group', label: 'Group', hint: 'Coach templates shared with a group.' },
];

export function inScope(template, scope) {
    const isAdmin = !!template.is_admin_template;
    switch (scope) {
        case 'admin':
            return isAdmin;
        case 'shared':
            return !isAdmin && (template.shared_with || []).length > 0;
        case 'group':
            return !isAdmin && (template.shared_with_groups || []).length > 0;
        case 'personal':
            return !isAdmin && (template.visibility || 'private') === 'private';
        default:
            return true;
    }
}

export function scopeCounts(templates) {
    return Object.fromEntries(
        TEMPLATE_SCOPES.map((s) => [s.value, templates.filter((t) => inScope(t, s.value)).length]),
    );
}

/** "Shared with 2 people and Sales team (group)", "Only the owner", … */
export function sharingText(template, groupNames = {}) {
    if (template.is_admin_template) return 'Every coach in the org';
    const people = (template.shared_with || []).length;
    const groups = (template.shared_with_groups || []).map((id) => groupNames[id] || 'a group');
    const parts = [];
    if (people) parts.push(`${people} ${people === 1 ? 'person' : 'people'}`);
    if (groups.length) parts.push(`${groups.join(', ')} (group)`);
    return parts.length ? `Shared with ${parts.join(' and ')}` : 'Only the owner';
}
